import type { Scene } from "@babylonjs/core/scene";
import type { Mesh } from "@babylonjs/core/Meshes/mesh";
import { CreateGround } from "@babylonjs/core/Meshes/Builders/groundBuilder";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import type { ZoneState } from "@arena/shared";

/** Frames of the asp's slither, side by side in one strip (PixelLab, docs/personaggi/cleopatra.md). */
const ASP_FRAMES = 4;

/** A flat sprite on the floor, drawn as it is (no lights): the asp and the bath. */
function floorSprite(scene: Scene, name: string, size: number, file: string): { mesh: Mesh; material: StandardMaterial; texture: Texture } {
  const mesh = CreateGround(name, { width: size, height: size }, scene);
  mesh.isPickable = false;
  const texture = new Texture(`/proto2d/cleopatra/${file}`, scene, true, true, Texture.NEAREST_SAMPLINGMODE);
  texture.hasAlpha = true;
  texture.wrapU = texture.wrapV = Texture.CLAMP_ADDRESSMODE;
  const material = new StandardMaterial(name, scene);
  material.diffuseTexture = texture;
  material.useAlphaFromDiffuseTexture = true;
  material.disableLighting = true;
  material.emissiveColor = Color3.White();
  material.backFaceCulling = false;
  mesh.material = material;
  return { mesh, material, texture };
}

/**
 * Cleopatra's asp (D45): a sand-coloured snake slithering along the floor,
 * seen from above, the head the way it goes. It lies flat under the shot,
 * which crawls a hand above the ground: a jump clears it.
 */
export class AspView {
  private readonly mesh: Mesh;
  private readonly material: StandardMaterial;
  private readonly texture: Texture;
  private age = 0;

  constructor(scene: Scene) {
    const sprite = floorSprite(scene, "asp", 1.6, "asp.png");
    this.mesh = sprite.mesh;
    this.material = sprite.material;
    this.texture = sprite.texture;
    this.texture.uScale = 1 / ASP_FRAMES;
  }

  update(x: number, y: number, z: number, fx: number, fz: number, dt: number): void {
    this.age += dt;
    this.mesh.position.set(x, y - 0.27, z);
    if (fx !== 0 || fz !== 0) this.mesh.rotation.y = Math.atan2(fx, fz);
    this.texture.uOffset = (Math.floor(this.age * 10) % ASP_FRAMES) / ASP_FRAMES;
  }

  dispose(): void {
    this.mesh.dispose();
    this.material.dispose();
    this.texture.dispose();
  }
}

/**
 * The bath of asses' milk (D45): a round pool of milk and rose petals in a gold
 * and lapis rim, fading in and out; its radius is the server's.
 */
export class MilkBathView {
  private readonly mesh: Mesh;
  private readonly material: StandardMaterial;
  private readonly texture: Texture;
  private age = 0;

  constructor(scene: Scene) {
    const sprite = floorSprite(scene, "milk-bath", 2, "bath.png");
    this.mesh = sprite.mesh;
    this.material = sprite.material;
    this.texture = sprite.texture;
  }

  update(zone: ZoneState, dt: number): void {
    this.age += dt;
    this.mesh.position.set(zone.x, zone.y + 0.06, zone.z);
    this.mesh.scaling.set(zone.radius, 1, zone.radius);
    this.material.alpha = Math.min(1, this.age / 0.25, zone.ticks / 10);
  }

  dispose(): void {
    this.mesh.dispose();
    this.material.dispose();
    this.texture.dispose();
  }
}
